import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { db } from "@/config/firebase";
import { collection, query, orderBy, getDocs, updateDoc, doc, arrayUnion, deleteDoc } from "firebase/firestore";
import { ArrowLeft, X, User, Trash2, ScanFace, Loader2, Check, CheckCircle, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as faceapi from 'face-api.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Gallery({ embedded = false }) {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [users, setUsers] = useState([]);
  const [filterPerson, setFilterPerson] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [tagInput, setTagInput] = useState("");
  
  // Face detection states
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [detectingFaces, setDetectingFaces] = useState(false);
  const [detectedFaces, setDetectedFaces] = useState([]);
  const [showFaceModal, setShowFaceModal] = useState(false);
  const [assigningFace, setAssigningFace] = useState(null);
  const [savingFaces, setSavingFaces] = useState(false);
  const [facesSaved, setFacesSaved] = useState(false);
  const imageRef = useRef(null);

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model';
        
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
        
        setModelsLoaded(true);
        console.log('Face detection models loaded');
      } catch (err) {
        console.error('Failed to load face detection models:', err);
      }
    };
    
    loadModels();
  }, []);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load users
        const usersSnap = await getDocs(collection(db, "users"));
        const usersList = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setUsers(usersList);

        // Load gallery images
        const gallerySnap = await getDocs(
          query(collection(db, "team_gallery"), orderBy("created_at", "desc"))
        );
        setImages(gallerySnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading gallery:', err);
      }
      setLoading(false);
    };
    
    loadData();
  }, []);

  const getUserName = (uid) => {
    const u = users.find(x => (x.auth_uid || x.id) === uid);
    return u?.display_name || u?.name || 'Unbekannt';
  };

  const tagPerson = async (imageId, personId) => {
    try {
      await updateDoc(doc(db, "team_gallery", imageId), {
        persons: arrayUnion(personId)
      });
      setImages(prev => prev.map(img => 
        img.id === imageId 
          ? { ...img, persons: [...(img.persons || []), personId] }
          : img
      ));
      setTagging(false);
      setTagInput("");
    } catch (err) {
      alert('Fehler beim Taggen');
    }
  };

  const deleteImage = async (imageId) => {
    if (!confirm('Bild wirklich löschen?')) return;
    try {
      await deleteDoc(doc(db, "team_gallery", imageId));
      setImages(prev => prev.filter(img => img.id !== imageId));
      setSelectedImage(null);
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  // Detect faces in the selected image
  const detectFaces = async () => {
    if (!modelsLoaded || !selectedImage || !imageRef.current) {
      alert('Gesichtserkennung noch nicht bereit');
      return;
    }
    
    setDetectingFaces(true);
    setDetectedFaces([]);
    
    try {
      const img = imageRef.current;
      
      // Detect all faces with landmarks
      const detections = await faceapi
        .detectAllFaces(img, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptors();
      
      if (detections.length === 0) {
        alert('Keine Gesichter erkannt');
        setDetectingFaces(false);
        return;
      }
      
      // Calculate face positions relative to image dimensions
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;
      
      const faces = detections.map((det, idx) => ({
        id: idx,
        box: {
          x: (det.detection.box.x / imgWidth) * 100,
          y: (det.detection.box.y / imgHeight) * 100,
          width: (det.detection.box.width / imgWidth) * 100,
          height: (det.detection.box.height / imgHeight) * 100,
        },
        descriptor: Array.from(det.descriptor),
        assignedTo: null
      }));
      
      setDetectedFaces(faces);
      setShowFaceModal(true);
      
    } catch (err) {
      console.error('Face detection error:', err);
      alert('Fehler bei der Gesichtserkennung');
    }
    
    setDetectingFaces(false);
  };

  // Assign a detected face to a user
  const assignFaceToUser = async (faceIdx, userId) => {
    const updatedFaces = detectedFaces.map((face, idx) => 
      idx === faceIdx ? { ...face, assignedTo: userId } : face
    );
    setDetectedFaces(updatedFaces);
    
    // Add the person to the image
    if (userId && !selectedImage.persons?.includes(userId)) {
      await tagPerson(selectedImage.id, userId);
    }
    
    setAssigningFace(null);
  };

  // Save face descriptors for future recognition
  const saveFaceDescriptors = async () => {
    setSavingFaces(true);
    setFacesSaved(false);
    
    try {
      const facesToSave = detectedFaces.filter(f => f.assignedTo);
      
      for (const face of facesToSave) {
        // Save face descriptor to user's profile for future matching
        const userRef = doc(db, "users", face.assignedTo);
        await updateDoc(userRef, {
          face_descriptors: arrayUnion(face.descriptor)
        });
      }
      
      // Mark image as processed
      await updateDoc(doc(db, "team_gallery", selectedImage.id), {
        faces_detected: true,
        face_count: detectedFaces.length
      });
      
      // Update local state
      setImages(prev => prev.map(img => 
        img.id === selectedImage.id 
          ? { ...img, faces_detected: true, face_count: detectedFaces.length }
          : img
      ));
      
      setFacesSaved(true);
      
      // Auto-close after showing success
      setTimeout(() => {
        setShowFaceModal(false);
        setDetectedFaces([]);
        setFacesSaved(false);
      }, 1500);
      
    } catch (err) {
      console.error('Error saving face descriptors:', err);
      alert('Fehler beim Speichern');
    }
    
    setSavingFaces(false);
  };

  // Filter images by person and search query
  const filteredImages = images.filter(img => {
    // Filter by person
    if (filterPerson && !img.persons?.includes(filterPerson)) {
      return false;
    }
    
    // Filter by search query (search in person names)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const personNames = (img.persons || [])
        .map(pid => {
          const u = users.find(x => x.id === pid);
          return (u?.display_name || u?.name || '').toLowerCase();
        });
      
      // Check if any person name matches
      if (!personNames.some(name => name.includes(query))) {
        return false;
      }
    }
    
    return true;
  });

  // Get all persons that appear in images (for filter buttons)
  const personsInGallery = users.filter(u => 
    images.some(img => img.persons?.includes(u.id))
  );

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return '';
    return timestamp.toDate().toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Group images by date
  const groupedImages = filteredImages.reduce((acc, img) => {
    const date = formatDate(img.created_at);
    if (!acc[date]) acc[date] = [];
    acc[date].push(img);
    return acc;
  }, {});

  return (
    <div className={embedded ? "text-rose-50" : "min-h-screen bg-[#0a0608] text-rose-50 pb-24"}>
      {/* Header */}
      <header className={embedded ? "px-1 py-2" : "px-4 py-3 bg-[#12090c] border-b border-white/5 sticky top-0 z-10"}>
        <div className="flex items-center gap-3">
          {!embedded && (
            <button onClick={() => navigate('/')} className="p-1">
              <ArrowLeft size={20} />
            </button>
          )}
          <div className="flex-1">
            {!embedded && <h1 className="font-semibold">Team Galerie</h1>}
            <p className="text-xs text-rose-200/50">
              {filteredImages.length} Bilder
              {modelsLoaded && <span className="ml-2 text-green-400">• Gesichtserkennung aktiv</span>}
            </p>
          </div>
          <button 
            onClick={() => setShowSearch(!showSearch)}
            className={`p-2 rounded-lg transition-colors ${showSearch ? 'bg-rose-600' : 'bg-white/5'}`}
          >
            <Search size={18} />
          </button>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="mt-3 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-rose-200/40" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Person suchen..."
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm placeholder:text-rose-200/40"
              autoFocus
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-200/40 hover:text-rose-200"
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}

        {/* Person Filter Chips */}
        <div className="mt-3 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          <button
            onClick={() => { setFilterPerson(null); setSearchQuery(''); }}
            className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              !filterPerson && !searchQuery ? 'bg-rose-600 text-white' : 'bg-white/5 text-rose-200/60'
            }`}
          >
            Alle
          </button>
          {personsInGallery.map(u => (
            <button
              key={u.id}
              onClick={() => { setFilterPerson(u.id); setSearchQuery(''); }}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                filterPerson === u.id ? 'bg-rose-600 text-white' : 'bg-white/5 text-rose-200/60'
              }`}
            >
              <User size={12} />
              {u.display_name || u.name || 'Unbekannt'}
            </button>
          ))}
        </div>
      </header>

      {/* Gallery Grid */}
      <div className="px-2 py-4">
        {loading ? (
          <div className="text-center py-8 text-rose-200/40">Lade Bilder...</div>
        ) : filteredImages.length === 0 ? (
          <div className="text-center py-8 text-rose-200/40">
            <p>Keine Bilder in der Galerie</p>
            <p className="text-sm mt-1">Speichere Bilder aus dem Chat!</p>
          </div>
        ) : (
          Object.entries(groupedImages).map(([date, imgs]) => (
            <div key={date} className="mb-6">
              <h2 className="text-sm font-semibold text-rose-200/50 mb-2 px-1">{date}</h2>
              <div className="grid grid-cols-3 gap-1">
                {imgs.map(img => (
                  <div 
                    key={img.id}
                    onClick={() => setSelectedImage(img)}
                    className="aspect-square bg-[#12090c] rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity relative"
                  >
                    <GalleryImage imageUrl={img.image_url} />
                    {img.persons?.length > 0 && (
                      <div className="absolute bottom-1 right-1 bg-black/70 rounded-full px-1.5 py-0.5 flex items-center gap-1">
                        <User size={10} />
                        <span className="text-xs">{img.persons.length}</span>
                      </div>
                    )}
                    {img.faces_detected && (
                      <div className="absolute top-1 right-1 bg-green-500/70 rounded-full p-1">
                        <ScanFace size={10} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex flex-col"
          onClick={() => { setSelectedImage(null); setShowFaceModal(false); setDetectedFaces([]); }}
        >
          {/* Modal Header */}
          <div className="p-4 flex items-center justify-between" onClick={e => e.stopPropagation()}>
            <button onClick={() => { setSelectedImage(null); setShowFaceModal(false); setDetectedFaces([]); }}>
              <X size={24} />
            </button>
            <div className="flex items-center gap-2">
              {modelsLoaded && (
                <button 
                  onClick={detectFaces}
                  disabled={detectingFaces}
                  className="flex items-center gap-1 text-sm bg-purple-600/30 border border-purple-500/30 px-3 py-1.5 rounded-lg disabled:opacity-50"
                >
                  {detectingFaces ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <ScanFace size={16} />
                  )}
                  {detectingFaces ? 'Scanne...' : 'Gesichter'}
                </button>
              )}
              <button 
                onClick={() => setTagging(true)}
                className="flex items-center gap-1 text-sm bg-white/10 px-3 py-1.5 rounded-lg"
              >
                <User size={16} />
                Taggen
              </button>
              {(role === 'admin' || selectedImage.saved_by === user?.uid) && (
                <button 
                  onClick={() => deleteImage(selectedImage.id)}
                  className="text-red-400 p-2"
                >
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Image with face boxes */}
          <div className="flex-1 flex items-center justify-center p-4 relative" onClick={e => e.stopPropagation()}>
            <div className="relative max-w-full max-h-full">
              <GalleryImage 
                imageUrl={selectedImage.image_url} 
                className="max-w-full max-h-[60vh] object-contain rounded-lg"
                imgRef={imageRef}
              />
              
              {/* Face detection boxes */}
              {detectedFaces.map((face, idx) => (
                <div
                  key={idx}
                  className={`absolute border-2 rounded ${
                    face.assignedTo ? 'border-green-400' : 'border-yellow-400'
                  }`}
                  style={{
                    left: `${face.box.x}%`,
                    top: `${face.box.y}%`,
                    width: `${face.box.width}%`,
                    height: `${face.box.height}%`,
                  }}
                  onClick={() => setAssigningFace(idx)}
                >
                  {face.assignedTo && (
                    <div className="absolute -bottom-6 left-0 right-0 text-center">
                      <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded">
                        {getUserName(face.assignedTo)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Face assignment panel */}
          {showFaceModal && detectedFaces.length > 0 && (
            <div className="p-4 bg-[#12090c] border-t border-white/10" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">
                  <ScanFace size={16} className="inline mr-2" />
                  {detectedFaces.length} Gesicht(er) erkannt
                </p>
                <button
                  onClick={saveFaceDescriptors}
                  disabled={!detectedFaces.some(f => f.assignedTo) || savingFaces || facesSaved}
                  className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg transition-colors ${
                    facesSaved 
                      ? 'bg-green-600 text-white' 
                      : 'bg-green-600 disabled:opacity-50'
                  }`}
                >
                  {facesSaved ? (
                    <>
                      <CheckCircle size={16} />
                      Gespeichert ✓
                    </>
                  ) : savingFaces ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Speichern
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-rose-200/50">
                Tippe auf die gelben Rahmen im Bild, um Personen zuzuweisen
              </p>
            </div>
          )}

          {/* Tagged Persons */}
          {selectedImage.persons?.length > 0 && !showFaceModal && (
            <div className="p-4" onClick={e => e.stopPropagation()}>
              <p className="text-xs text-rose-200/50 mb-2">Personen:</p>
              <div className="flex flex-wrap gap-2">
                {selectedImage.persons.map(pid => (
                  <span key={pid} className="px-3 py-1 rounded-full bg-rose-600/30 text-sm">
                    {getUserName(pid)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="p-4 text-sm text-rose-200/50" onClick={e => e.stopPropagation()}>
            Gespeichert von {getUserName(selectedImage.saved_by)} • {formatDate(selectedImage.created_at)}
          </div>

          {/* Face Assignment Modal */}
          {assigningFace !== null && (
            <div 
              className="absolute inset-0 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setAssigningFace(null)}
            >
              <div 
                className="bg-[#12090c] rounded-xl p-4 w-full max-w-sm max-h-[60vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="font-semibold mb-3">Wer ist das?</h3>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Name suchen..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 mb-3 text-sm"
                  autoFocus
                />
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {users
                    .filter(u => {
                      const name = (u.display_name || u.name || '').toLowerCase();
                      return name.includes(tagInput.toLowerCase());
                    })
                    .slice(0, 15)
                    .map(u => (
                      <button
                        key={u.id}
                        onClick={() => assignFaceToUser(assigningFace, u.id)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-2"
                      >
                        <User size={16} className="text-rose-200/50" />
                        {u.display_name || u.name}
                      </button>
                    ))
                  }
                </div>
                <button
                  onClick={() => setAssigningFace(null)}
                  className="w-full mt-3 py-2 bg-white/5 rounded-lg text-sm"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}

          {/* Manual Tagging Modal */}
          {tagging && (
            <div 
              className="absolute inset-0 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setTagging(false)}
            >
              <div 
                className="bg-[#12090c] rounded-xl p-4 w-full max-w-sm max-h-[60vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="font-semibold mb-3">Person taggen</h3>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Name suchen..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 mb-3 text-sm"
                />
                <div className="space-y-1">
                  {users
                    .filter(u => {
                      const name = (u.display_name || u.name || '').toLowerCase();
                      return name.includes(tagInput.toLowerCase()) && !selectedImage.persons?.includes(u.id);
                    })
                    .slice(0, 10)
                    .map(u => (
                      <button
                        key={u.id}
                        onClick={() => tagPerson(selectedImage.id, u.id)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        {u.display_name || u.name}
                      </button>
                    ))
                  }
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Component to handle both regular and chunked images
function GalleryImage({ imageUrl, className = "w-full h-full object-cover", imgRef }) {
  const [loadedUrl, setLoadedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!imageUrl) return;
    
    // Check if it's a chunked image
    if (imageUrl.startsWith('chunked:')) {
      const parts = imageUrl.split(':');
      const imgId = parts[1];
      
      setLoading(true);
      fetch(`${BACKEND_URL}/api/chat/image/${imgId}`)
        .then(res => res.json())
        .then(data => {
          if (data.image_b64) {
            setLoadedUrl(`data:image/png;base64,${data.image_b64}`);
          } else {
            setError(true);
          }
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    } else {
      setLoadedUrl(imageUrl);
    }
  }, [imageUrl]);

  if (loading) {
    return (
      <div className={`${className} bg-white/10 flex items-center justify-center`}>
        <Loader2 className="animate-spin text-pink-400" size={24} />
      </div>
    );
  }

  if (error || !loadedUrl) {
    return (
      <div className={`${className} bg-white/10 flex items-center justify-center text-rose-200/50`}>
        <span className="text-xs">Fehler</span>
      </div>
    );
  }

  return (
    <img 
      ref={imgRef}
      src={loadedUrl} 
      alt="" 
      className={className}
      crossOrigin="anonymous"
      loading="lazy"
    />
  );
}
